import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { EntityLookup, type EntityLookupValue } from "@/components/shared/EntityLookup";
import { getInstituicaoLookup } from "@/features/cobranca/api";
import { DataTable, type RowAction } from "@/components/data-table/DataTable";
import { usePagedQuery } from "@/components/data-table/usePagedQuery";
import { toLocalDateTimeString, formatDateHoraBr } from "@/lib/format";
import {
  deleteMensageria,
  getMensageria,
  saveMensageria,
  MENSAGERIA_PATH,
  type Mensageria,
  type MensageriaFiltro,
} from "./api";
import { mensageriaSchema, type MensageriaFormValues } from "./schema";

// Porte de app/controllers/consulta-mensageria.controller.js +
// app/templates/mensageria/consulta-mensageria.html. Único ponto de
// atenção do legado: `buscar()` ajusta dataValidadeTermino para 23:59:59.999
// antes de enviar (torna o filtro de "até" inclusivo). Reproduzido aqui no
// próprio onChange do campo, não no clique de Buscar — mesmo efeito líquido,
// sem depender de um Date vazando para JSON (ver lib/format.ts).
const QUERY_KEY = ["mensageria"];

const TIPO_LABEL: Record<string, string> = { A: "Alerta", E: "Erro", N: "Normal" };

const columns: ColumnDef<Mensageria, unknown>[] = [
  { accessorKey: "idMensageria", header: "ID", enableSorting: true },
  { accessorKey: "texto", header: "Texto", enableSorting: true },
  {
    accessorKey: "tipo",
    header: "Tipo",
    enableSorting: true,
    cell: ({ getValue }) => TIPO_LABEL[String(getValue() ?? "")] ?? String(getValue() ?? ""),
  },
  {
    accessorKey: "dataValidade",
    header: "Validade",
    enableSorting: true,
    cell: ({ getValue }) => formatDateHoraBr(getValue() as string | null),
  },
  {
    id: "instituicao",
    header: "Instituição",
    accessorFn: (row) => row.instituicao?.nomeFantasia ?? "Geral",
  },
  {
    accessorKey: "seLiberado",
    header: "Liberado",
    enableSorting: true,
    cell: ({ getValue }) => (getValue() === "S" ? "Sim" : "Não"),
  },
];

export default function ConsultaMensageria() {
  const qc = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  // Registro como veio do GET; o zod descarta chaves fora do schema, então o
  // salvamento sobrepõe os valores do form a ele (ver D14).
  const [registroOriginal, setRegistroOriginal] = useState<Mensageria | null>(null);
  // Fora do react-hook-form (ver EntityLookup). Vazio = mensagem geral.
  const [instituicao, setInstituicao] = useState<EntityLookupValue | null>(null);

  const query = usePagedQuery<Mensageria, MensageriaFiltro>({
    key: QUERY_KEY,
    path: MENSAGERIA_PATH,
    initialFiltro: {},
  });

  const form = useForm<MensageriaFormValues>({
    resolver: zodResolver(mensageriaSchema),
    defaultValues: { texto: "", tipo: "N", dataValidade: "", seLiberado: "S" },
  });

  const save = useMutation({
    mutationFn: saveMensageria,
    onSuccess: () => {
      toast.success("Mensageria salva.");
      qc.invalidateQueries({ queryKey: QUERY_KEY });
      setDialogOpen(false);
    },
  });

  const excluir = useMutation({
    mutationFn: deleteMensageria,
    onSuccess: () => {
      toast.success("Mensageria excluída.");
      qc.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });

  function openCreate() {
    form.reset({ idMensageria: undefined, texto: "", tipo: "N", dataValidade: "", seLiberado: "S" });
    setInstituicao(null);
    setRegistroOriginal(null);
    setDialogOpen(true);
  }

  async function openEdit(row: Mensageria) {
    const registro = await getMensageria(row.idMensageria);
    form.reset({ ...registro, dataValidade: registro.dataValidade ?? "" });
    setRegistroOriginal(registro);
    setInstituicao(registro.instituicaoId ? await getInstituicaoLookup(registro.instituicaoId) : null);
    setDialogOpen(true);
  }

  const actions: RowAction<Mensageria>[] = [
    { label: "Editar", icon: Pencil, onClick: openEdit },
    {
      label: "Excluir",
      icon: Trash2,
      variant: "destructive",
      onClick: (row) => excluir.mutate(row.idMensageria),
      confirm: {
        title: "Excluir mensageria?",
        description: (row) => `"${row.texto}" será removida permanentemente.`,
        confirmLabel: "Excluir",
      },
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold text-foreground">Mensageria</h1>
        <Button onClick={openCreate}>
          <Plus className="mr-2 h-4 w-4" /> Adicionar Novo
        </Button>
      </div>

      <DataTable
        query={query}
        columns={columns}
        actions={actions}
        getRowId={(r) => r.idMensageria}
        exportFilename="mensageria"
        emptyMessage="Nenhuma mensageria encontrada."
        toolbar={
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-56">
              <Label htmlFor="f-texto">Texto</Label>
              <Input
                id="f-texto"
                value={query.draft.texto ?? ""}
                onChange={(e) => query.setDraft((f) => ({ ...f, texto: e.target.value }))}
                onKeyDown={(e) => e.key === "Enter" && query.search()}
              />
            </div>
            <div className="w-64">
              <EntityLookup
                label="Instituição"
                entityPath="instituicao/Instituicao"
                searchByCodeAndName
                displayCode
                value={query.draft.instituicao ?? null}
                onChange={(v) =>
                  query.setDraft((f) => ({ ...f, instituicao: v ?? undefined, instituicaoId: v?.id }))
                }
              />
            </div>
            <div className="w-40">
              <Label htmlFor="f-tipo">Tipo</Label>
              <Select
                value={query.draft.tipo ?? "__all__"}
                onValueChange={(v) => query.setDraft((f) => ({ ...f, tipo: v === "__all__" ? undefined : v }))}
              >
                <SelectTrigger id="f-tipo">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todos</SelectItem>
                  <SelectItem value="A">Alerta</SelectItem>
                  <SelectItem value="E">Erro</SelectItem>
                  <SelectItem value="N">Normal</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-40">
              <Label htmlFor="f-liberado">Liberado</Label>
              <Select
                value={query.draft.seLiberado ?? "__all__"}
                onValueChange={(v) => query.setDraft((f) => ({ ...f, seLiberado: v === "__all__" ? undefined : v }))}
              >
                <SelectTrigger id="f-liberado">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todos</SelectItem>
                  <SelectItem value="S">Sim</SelectItem>
                  <SelectItem value="N">Não</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-44">
              <Label htmlFor="f-validade-inicio">Validade de</Label>
              <Input
                id="f-validade-inicio"
                type="date"
                value={query.draft.dataValidadeInicio?.slice(0, 10) ?? ""}
                onChange={(e) =>
                  query.setDraft((f) => ({
                    ...f,
                    dataValidadeInicio: e.target.value ? toLocalDateTimeString(e.target.value) : undefined,
                  }))
                }
              />
            </div>
            <div className="w-44">
              <Label htmlFor="f-validade-termino">Validade até</Label>
              <Input
                id="f-validade-termino"
                type="date"
                value={query.draft.dataValidadeTermino?.slice(0, 10) ?? ""}
                onChange={(e) =>
                  query.setDraft((f) => ({
                    ...f,
                    dataValidadeTermino: e.target.value ? toLocalDateTimeString(e.target.value, true) : undefined,
                  }))
                }
              />
            </div>
            <Button onClick={query.search}>Buscar</Button>
          </div>
        }
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mensageria</DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(
                // `instituicao` (entity aninhada) fica de fora: quem manda é instituicaoId.
                (values) => {
                  const original: Partial<Mensageria> = { ...registroOriginal };
                  delete original.instituicao;
                  save.mutate({ ...original, ...values, instituicaoId: instituicao?.id ?? null });
                },
                () => toast.error("Existem campos inválidos. Verifique os campos destacados em vermelho."),
              )}
              className="space-y-4"
            >
              <FormField
                control={form.control}
                name="texto"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Texto</FormLabel>
                    <FormControl>
                      <Textarea maxLength={255} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid gap-4 sm:grid-cols-3">
                <FormField
                  control={form.control}
                  name="tipo"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tipo</FormLabel>
                      <Select value={field.value} onValueChange={(v) => v && field.onChange(v)}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="A">Alerta</SelectItem>
                          <SelectItem value="E">Erro</SelectItem>
                          <SelectItem value="N">Normal</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="dataValidade"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Validade</FormLabel>
                      <FormControl>
                        <Input type="datetime-local" {...field} value={field.value ?? ""} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="seLiberado"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Liberado</FormLabel>
                      <Select value={field.value} onValueChange={(v) => v && field.onChange(v)}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="S">Sim</SelectItem>
                          <SelectItem value="N">Não</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="space-y-1">
                <EntityLookup
                  label="Instituição"
                  entityPath="instituicao/Instituicao"
                  searchByCodeAndName
                  displayCode
                  value={instituicao}
                  onChange={setInstituicao}
                />
                <p className="text-xs text-muted-foreground">
                  Opcional. Sem instituição, a mensagem vale para todas (mensagem geral).
                </p>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={save.isPending}>
                  {save.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {save.isPending ? "Salvando..." : "Salvar"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
